import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    Box,
    CheckCircle2,
    ExternalLink,
    Globe2,
    LineChart,
    Monitor,
    Package,
    Play,
    Search,
    Settings2,
    ShoppingCart,
    Smartphone,
    Store,
    Target,
    TrendingUp,
    Zap,
} from 'lucide-react';
import Breadcrumbs, { type Crumb } from '../../navigation/breadcrumbs/Breadcrumbs';

type WebCaseStudyIconComponent = React.ComponentType<{
    className?: string;
    size?: number;
    style?: CSSProperties;
    strokeWidth?: number;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

export type WebCaseStudyIconRenderer = React.ElementType<{
    className?: string;
    style?: CSSProperties;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

const iconRegistry = {
    arrowRight: ArrowRight,
    box: Box,
    check: CheckCircle2,
    external: ExternalLink,
    globe: Globe2,
    lineChart: LineChart,
    monitor: Monitor,
    package: Package,
    play: Play,
    search: Search,
    settings: Settings2,
    shoppingCart: ShoppingCart,
    smartphone: Smartphone,
    store: Store,
    target: Target,
    trendingUp: TrendingUp,
    zap: Zap,
} satisfies Record<string, WebCaseStudyIconComponent>;

export type WebCaseStudyIconToken = keyof typeof iconRegistry;

export const webCaseStudyIconTokens = Object.keys(iconRegistry) as WebCaseStudyIconToken[];

export type WebCaseStudyLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type WebCaseStudyImageSource =
    | string
    | {
          src?: string;
          url?: string;
          original?: string;
          thumbnail?: string;
          alt?: string;
          title?: string;
          width?: number;
          height?: number;
          loading?: 'eager' | 'lazy';
          decorative?: boolean;
          objectPosition?: string;
          position?: string;
      };

export type WebCaseStudyActionVariant = 'solid' | 'outline' | 'ghost';

export type WebCaseStudyAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    variant?: WebCaseStudyActionVariant;
    icon?: React.ReactNode;
    iconComponent?: WebCaseStudyIconRenderer;
    iconToken?: WebCaseStudyIconToken;
};

export type WebCaseStudyStat = {
    id?: string | number;
    value: React.ReactNode;
    label?: React.ReactNode;
    description?: React.ReactNode;
    ariaLabel?: string;
    icon?: React.ReactNode;
    iconComponent?: WebCaseStudyIconRenderer;
    iconToken?: WebCaseStudyIconToken;
    accentColor?: string;
    accentColour?: string;
};

export type WebCaseStudyDetail = {
    id?: string | number;
    label: React.ReactNode;
    value: React.ReactNode;
};

export type WebCaseStudyChecklistItem =
    | React.ReactNode
    | {
          id?: string | number;
          label: React.ReactNode;
          ariaLabel?: string;
          icon?: React.ReactNode;
          iconComponent?: WebCaseStudyIconRenderer;
          iconToken?: WebCaseStudyIconToken;
      };

export type WebCaseStudyScreenshotViewport = 'desktop' | 'mobile';

export type WebCaseStudyScreenshot = {
    id?: string | number;
    title: React.ReactNode;
    image?: WebCaseStudyImageSource;
    alt?: string;
    viewport?: WebCaseStudyScreenshotViewport;
    icon?: React.ReactNode;
    iconComponent?: WebCaseStudyIconRenderer;
    iconToken?: WebCaseStudyIconToken;
    imagePosition?: string;
};

export type WebCaseStudyResult = {
    id?: string | number;
    value: React.ReactNode;
    label?: React.ReactNode;
    description?: React.ReactNode;
    ariaLabel?: string;
    icon?: React.ReactNode;
    iconComponent?: WebCaseStudyIconRenderer;
    iconToken?: WebCaseStudyIconToken;
    accentColor?: string;
    accentColour?: string;
};

export type WebCaseStudyService =
    | React.ReactNode
    | {
          id?: string | number;
          label: React.ReactNode;
          ariaLabel?: string;
          textColor?: string;
          textColour?: string;
          backgroundColor?: string;
          backgroundColour?: string;
      };

export type WebCaseStudyProjectTag =
    | React.ReactNode
    | {
          id?: string | number;
          label: React.ReactNode;
          ariaLabel?: string;
          textColor?: string;
          textColour?: string;
          backgroundColor?: string;
          backgroundColour?: string;
      };

export type WebCaseStudyCta = {
    title?: React.ReactNode;
    description?: React.ReactNode;
    primaryAction?: WebCaseStudyAction | false;
    secondaryAction?: WebCaseStudyAction | false;
};

export type WebCaseStudyColorToken =
    | 'background'
    | 'surface'
    | 'surfaceSoft'
    | 'text'
    | 'textSoft'
    | 'textMuted'
    | 'accent'
    | 'accentStrong'
    | 'accentSoft'
    | 'border'
    | 'shadow';

export type WebCaseStudyColors = Partial<Record<WebCaseStudyColorToken, string>>;

export type WebCaseStudyBreadcrumbItem = {
    label: React.ReactNode;
    href?: string;
};

export type WebCaseStudyProps = {
    Link?: WebCaseStudyLinkComponent;
    breadcrumbItems?: WebCaseStudyBreadcrumbItem[];
    breadcrumbLabel?: React.ReactNode;
    breadcrumbHref?: string;
    projectName?: React.ReactNode;
    title?: React.ReactNode;
    titleHighlight?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    heroDesktopImage?: WebCaseStudyImageSource;
    heroMobileImage?: WebCaseStudyImageSource;
    heroDesktopAlt?: string;
    heroMobileAlt?: string;
    heroDesktopImagePosition?: string;
    heroMobileImagePosition?: string;
    primaryAction?: WebCaseStudyAction | false;
    secondaryAction?: WebCaseStudyAction | false;
    stats?: WebCaseStudyStat[];
    overviewTitle?: React.ReactNode;
    overview?: React.ReactNode;
    overviewParagraphs?: React.ReactNode[];
    details?: WebCaseStudyDetail[];
    detailsIcon?: React.ReactNode;
    detailsIconComponent?: WebCaseStudyIconRenderer;
    detailsIconToken?: WebCaseStudyIconToken;
    deliveredTitle?: React.ReactNode;
    deliveredItems?: WebCaseStudyChecklistItem[];
    screenshotsTitle?: React.ReactNode;
    screenshotsAction?: WebCaseStudyAction | false;
    screenshots?: WebCaseStudyScreenshot[];
    projectInsightsTitle?: React.ReactNode;
    projectInsights?: React.ReactNode;
    projectInsightParagraphs?: React.ReactNode[];
    projectTagsTitle?: React.ReactNode;
    projectTags?: WebCaseStudyProjectTag[];
    resultsTitle?: React.ReactNode;
    results?: WebCaseStudyResult[];
    servicesTitle?: React.ReactNode;
    services?: WebCaseStudyService[];
    cta?: WebCaseStudyCta | false;
    colors?: WebCaseStudyColors;
    colours?: WebCaseStudyColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    className?: string;
    style?: CSSProperties;
    id?: string;
    ariaLabel?: string;
};

type WebCaseStudyStyle = CSSProperties &
    Record<`--web-case-study-${string}`, string | number | undefined>;

type NormalizedImage = {
    src?: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
    loading: 'eager' | 'lazy';
    decorative: boolean;
    position?: string;
};

const webCaseStudyTokens = {
    colors: {
        background: '#ffffff',
        surface: '#ffffff',
        surfaceSoft: '#f7fbf6',
        text: '#101426',
        textSoft: '#475167',
        textMuted: '#667085',
        accent: '#19a832',
        accentStrong: '#108625',
        accentSoft: 'rgba(25, 168, 50, 0.1)',
        border: 'rgba(16, 24, 40, 0.11)',
        shadow: '0 18px 50px rgba(16, 24, 40, 0.08)',
    },
    sizes: {
        maxWidth: 1440,
        desktopRatio: '16 / 10',
        mobileRatio: '9 / 19.5',
    },
} as const;

export const webCaseStudyImageRatios = {
    desktop: webCaseStudyTokens.sizes.desktopRatio,
    mobile: webCaseStudyTokens.sizes.mobileRatio,
} as const;

const DefaultLink: WebCaseStudyLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const defaultStats: WebCaseStudyStat[] = [
    { value: '200+', label: 'Products', iconToken: 'package' },
    { value: '220+', label: 'Indexed Pages', iconToken: 'search' },
    { value: 'Custom CMS', label: 'Easy to manage', iconToken: 'settings' },
    { value: 'Fast Mobile', label: 'Optimised for all devices', iconToken: 'zap' },
];

const defaultDeliveredItems: WebCaseStudyChecklistItem[] = [
    'Custom website design tailored to the brand',
    'Category-led navigation for easy browsing',
    'SEO-friendly page structure and metadata',
    'Product collection and detail pages',
    'Analytics and search console setup',
    'Performance optimisation for speed and mobile',
];

const defaultResults: WebCaseStudyResult[] = [
    { value: '+118%', label: 'Increase in Organic Traffic', iconToken: 'trendingUp' },
    { value: '+76%', label: 'Increase in Online Enquiries', iconToken: 'shoppingCart' },
    { value: 'Top 20', label: 'Rankings for Key Search Terms', iconToken: 'target' },
    { value: '90+', label: 'PageSpeed Score', iconToken: 'lineChart' },
];

const webCaseStudyCss = `
.web-case-study {
  --web-case-study-bg: ${webCaseStudyTokens.colors.background};
  --web-case-study-surface: ${webCaseStudyTokens.colors.surface};
  --web-case-study-surface-soft: ${webCaseStudyTokens.colors.surfaceSoft};
  --web-case-study-text: ${webCaseStudyTokens.colors.text};
  --web-case-study-text-soft: ${webCaseStudyTokens.colors.textSoft};
  --web-case-study-text-muted: ${webCaseStudyTokens.colors.textMuted};
  --web-case-study-accent: ${webCaseStudyTokens.colors.accent};
  --web-case-study-accent-strong: ${webCaseStudyTokens.colors.accentStrong};
  --web-case-study-accent-soft: ${webCaseStudyTokens.colors.accentSoft};
  --web-case-study-border: ${webCaseStudyTokens.colors.border};
  --web-case-study-shadow: ${webCaseStudyTokens.colors.shadow};
  --web-case-study-max-width: ${webCaseStudyTokens.sizes.maxWidth}px;
  --web-case-study-desktop-ratio: ${webCaseStudyTokens.sizes.desktopRatio};
  --web-case-study-mobile-ratio: ${webCaseStudyTokens.sizes.mobileRatio};
  width: 100%;
  min-width: 0;
  background: var(--web-case-study-bg);
  color: var(--web-case-study-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
  container-type: inline-size;
}
.web-case-study,
.web-case-study * {
  box-sizing: border-box;
}
.web-case-study-shell {
  width: 100%;
  max-width: var(--web-case-study-max-width);
  margin: 0 auto;
  padding: clamp(22px, 4vw, 58px) clamp(18px, 4vw, 60px);
}
.web-case-study-hero {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(360px, 1.08fr);
  gap: clamp(28px, 4.4vw, 74px);
  align-items: center;
  min-width: 0;
  padding: clamp(16px, 2.4vw, 28px) 0 clamp(20px, 3vw, 38px);
}
.web-case-study-hero-content {
  min-width: 0;
}
.web-case-study-breadcrumb {
  max-width: 100%;
  margin: 0 0 clamp(24px, 3vw, 44px);
  color: var(--web-case-study-text);
  font-size: 13px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
}
.web-case-study-breadcrumb-nav {
  width: 100%;
  padding: 0;
  border-radius: 0;
}
.web-case-study-breadcrumb-nav ol {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  margin: 0;
  padding: 0;
  color: var(--web-case-study-text);
  list-style: none;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
}
.web-case-study-breadcrumb-nav li {
  display: flex;
  align-items: center;
  gap: 11px;
}
.web-case-study-breadcrumb-nav li:not(:last-child) {
  margin-right: 11px;
}
.web-case-study-breadcrumb-nav a,
.web-case-study-breadcrumb-nav li > span {
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-decoration: none;
}
.web-case-study-breadcrumb-nav a {
  transition: color 160ms ease, text-decoration-color 160ms ease;
}
.web-case-study-breadcrumb-nav a:hover {
  color: var(--web-case-study-accent-strong);
  text-decoration: underline;
}
.web-case-study-breadcrumb-nav a:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--web-case-study-accent) 30%, transparent);
  outline-offset: 4px;
  border-radius: 4px;
}
.web-case-study-breadcrumb-nav li > span + span {
  margin: 0;
  color: var(--web-case-study-text-muted);
}
.web-case-study-title {
  max-width: 760px;
  margin: 0;
  color: var(--web-case-study-text);
  font-size: clamp(38px, 4.8vw, 64px);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-title-highlight {
  color: var(--web-case-study-accent);
}
.web-case-study-description {
  max-width: 650px;
  margin: 22px 0 0;
  color: var(--web-case-study-text-soft);
  font-size: clamp(16px, 1.35vw, 19px);
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
}
.web-case-study-actions {
  display: flex;
  max-width: 100%;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: clamp(28px, 3.2vw, 46px);
}
.web-case-study-action {
  --web-case-study-action-bg: var(--web-case-study-surface);
  --web-case-study-action-text: var(--web-case-study-accent);
  --web-case-study-action-border: color-mix(in srgb, var(--web-case-study-accent) 64%, var(--web-case-study-border));
  display: inline-flex;
  min-height: 58px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid var(--web-case-study-action-border);
  border-radius: 8px;
  background: var(--web-case-study-action-bg);
  color: var(--web-case-study-action-text);
  font-size: 15px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
  padding: 0 26px;
  text-decoration: none;
  box-shadow: 0 12px 24px rgba(16, 24, 40, 0.06);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}
.web-case-study-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(16, 24, 40, 0.1);
}
.web-case-study-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--web-case-study-accent) 34%, transparent);
  outline-offset: 3px;
}
.web-case-study-action--solid {
  --web-case-study-action-bg: linear-gradient(135deg, var(--web-case-study-accent), var(--web-case-study-accent-strong));
  --web-case-study-action-text: #ffffff;
  --web-case-study-action-border: var(--web-case-study-accent);
}
.web-case-study-action--ghost {
  --web-case-study-action-bg: transparent;
  --web-case-study-action-border: transparent;
  box-shadow: none;
  padding-inline: 4px;
}
.web-case-study-action-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.web-case-study-icon,
.web-case-study-icon svg {
  display: block;
  width: var(--web-case-study-icon-size, 18px);
  height: var(--web-case-study-icon-size, 18px);
  flex: 0 0 auto;
}
.web-case-study-hero-media {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(118px, 14vw, 184px);
  gap: clamp(14px, 2.2vw, 28px);
  align-items: center;
  justify-items: center;
  min-width: 0;
  min-height: clamp(330px, 37vw, 560px);
  padding: clamp(12px, 2vw, 28px) 0;
  isolation: isolate;
}
.web-case-study-hero-media::before {
  content: "";
  position: absolute;
  z-index: -1;
  top: 50%;
  right: 5%;
  width: min(74%, 520px);
  aspect-ratio: 1;
  border-radius: 999px;
  background: var(--web-case-study-accent-soft);
  transform: translateY(-52%);
}
.web-case-study-laptop {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 720px;
  justify-self: end;
}
.web-case-study-laptop-frame {
  position: relative;
  overflow: hidden;
  padding: clamp(8px, 1.1vw, 13px);
  border-radius: 13px 13px 7px 7px;
  background: linear-gradient(180deg, #0a0d13, #171b23);
  box-shadow: 0 28px 58px rgba(16, 24, 40, 0.18);
}
.web-case-study-laptop-camera {
  position: absolute;
  top: 4px;
  left: 50%;
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.28);
  transform: translateX(-50%);
}
.web-case-study-laptop-screen {
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--web-case-study-desktop-ratio);
  border-radius: 5px;
  background: #edf2f7;
}
.web-case-study-laptop-base {
  position: relative;
  width: 112%;
  height: clamp(13px, 1.8vw, 24px);
  margin: 0 0 0 -6%;
  border-radius: 0 0 999px 999px;
  background: linear-gradient(180deg, #f5f6f8, #bfc4cc 58%, #8d949e);
  box-shadow: 0 12px 22px rgba(16, 24, 40, 0.16);
}
.web-case-study-laptop-base::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  width: 22%;
  height: 42%;
  border-radius: 0 0 9px 9px;
  background: rgba(16, 24, 40, 0.14);
  transform: translateX(-50%);
}
.web-case-study-phone {
  position: relative;
  z-index: 2;
  width: min(100%, 188px);
  justify-self: start;
  align-self: center;
  padding: clamp(6px, 0.8vw, 9px);
  border-radius: clamp(22px, 2.8vw, 34px);
  background: linear-gradient(180deg, #07090d, #1b2028);
  box-shadow: 0 24px 42px rgba(16, 24, 40, 0.24);
  transform: translateY(clamp(8px, 2vw, 24px));
}
.web-case-study-phone::before {
  content: "";
  position: absolute;
  z-index: 2;
  top: clamp(6px, 0.8vw, 9px);
  left: 50%;
  width: 38%;
  height: clamp(10px, 1.2vw, 17px);
  border-radius: 0 0 12px 12px;
  background: #07090d;
  transform: translateX(-50%);
}
.web-case-study-phone-screen {
  position: relative;
  overflow: hidden;
  aspect-ratio: var(--web-case-study-mobile-ratio);
  border-radius: clamp(16px, 2vw, 25px);
  background: #eef3ef;
}
.web-case-study-device-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--web-case-study-image-position, top center);
}
.web-case-study-device-placeholder {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background:
    linear-gradient(180deg, #ffffff 0 12%, transparent 12%),
    linear-gradient(90deg, rgba(16, 24, 40, 0.1) 0 14%, transparent 14% 100%),
    repeating-linear-gradient(0deg, transparent 0 24px, rgba(25, 168, 50, 0.08) 24px 25px),
    linear-gradient(135deg, #f8fafc, #eaf6ed);
}
.web-case-study-device-placeholder::before,
.web-case-study-device-placeholder::after {
  content: "";
  position: absolute;
  border-radius: 8px;
  background: rgba(25, 168, 50, 0.16);
}
.web-case-study-device-placeholder::before {
  left: 18%;
  top: 22%;
  width: 62%;
  height: 22%;
}
.web-case-study-device-placeholder::after {
  left: 18%;
  top: 52%;
  width: 44%;
  height: 10%;
}
.web-case-study-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
  overflow: hidden;
  margin: clamp(10px, 2vw, 18px) 0 clamp(36px, 5vw, 70px);
  border: 1px solid var(--web-case-study-border);
  border-radius: 8px;
  background: var(--web-case-study-surface);
  box-shadow: var(--web-case-study-shadow);
}
.web-case-study-stat {
  --web-case-study-stat-accent: var(--web-case-study-accent);
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  min-width: 0;
  min-height: 104px;
  padding: 20px clamp(18px, 2.2vw, 38px);
  border-right: 1px solid var(--web-case-study-border);
}
.web-case-study-stat:last-child {
  border-right: 0;
}
.web-case-study-stat-icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: color-mix(in srgb, var(--web-case-study-stat-accent) 10%, #ffffff);
  color: var(--web-case-study-stat-accent);
}
.web-case-study-stat-value {
  margin: 0;
  color: var(--web-case-study-text);
  font-size: clamp(19px, 2vw, 28px);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-stat-label {
  margin: 5px 0 0;
  color: var(--web-case-study-text-soft);
  font-size: 14px;
  line-height: 1.25;
  font-weight: 650;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-main-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.92fr);
  gap: clamp(28px, 5vw, 76px);
  align-items: start;
  margin-bottom: clamp(34px, 5vw, 66px);
}
.web-case-study-section-title {
  margin: 0 0 18px;
  color: var(--web-case-study-text);
  font-size: clamp(24px, 2.2vw, 31px);
  line-height: 1.15;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-rich-text {
  display: grid;
  gap: 18px;
  color: var(--web-case-study-text);
}
.web-case-study-rich-text p {
  margin: 0;
  color: var(--web-case-study-text);
  font-size: 16px;
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
}
.web-case-study-rich-text ul,
.web-case-study-rich-text ol {
  display: grid;
  gap: 10px;
  margin: 0;
  padding-left: 22px;
  color: var(--web-case-study-text);
  font-size: 16px;
  line-height: 1.58;
  font-weight: 500;
  letter-spacing: 0;
}
.web-case-study-rich-text li {
  padding-left: 3px;
}
.web-case-study-rich-text strong,
.web-case-study-rich-text b {
  font-weight: 850;
}
.web-case-study-rich-text a {
  color: var(--web-case-study-accent-strong);
  font-weight: 750;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
}
.web-case-study-rich-text a:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--web-case-study-accent) 30%, transparent);
  outline-offset: 3px;
  border-radius: 4px;
}
.web-case-study-detail-panel {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  margin-top: 28px;
  padding: 18px 22px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--web-case-study-surface-soft), #ffffff);
}
.web-case-study-detail-icon {
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--web-case-study-accent-soft);
  color: var(--web-case-study-accent);
}
.web-case-study-detail-list {
  display: grid;
  gap: 5px;
  min-width: 0;
  margin: 0;
}
.web-case-study-detail-row {
  min-width: 0;
  color: var(--web-case-study-text);
  font-size: 14px;
  line-height: 1.35;
  font-weight: 650;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-detail-label {
  font-weight: 900;
}
.web-case-study-detail-row dt,
.web-case-study-detail-row dd {
  display: inline;
  margin: 0;
}
.web-case-study-detail-row dd {
  margin-left: 4px;
}
.web-case-study-delivered {
  padding-left: clamp(0px, 3vw, 38px);
  border-left: 1px solid var(--web-case-study-border);
}
.web-case-study-check-list {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.web-case-study-check-item {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  min-width: 0;
  color: var(--web-case-study-text);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 600;
  letter-spacing: 0;
}
.web-case-study-check-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  color: var(--web-case-study-accent);
}
.web-case-study-section-header {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}
.web-case-study-screenshots {
  margin-bottom: clamp(34px, 5vw, 66px);
}
.web-case-study-screenshot-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(16px, 2vw, 28px);
}
.web-case-study-screenshot-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--web-case-study-border);
  border-radius: 8px;
  background: var(--web-case-study-surface);
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.06);
}
.web-case-study-screenshot-media-wrap {
  display: flex;
  min-height: 0;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: linear-gradient(180deg, #ffffff, var(--web-case-study-surface-soft));
}
.web-case-study-screenshot-media {
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: var(--web-case-study-desktop-ratio);
  border: 1px solid var(--web-case-study-border);
  border-radius: 5px;
  background: #eef4ef;
}
.web-case-study-screenshot-card--mobile .web-case-study-screenshot-media {
  width: min(54%, 122px);
  aspect-ratio: var(--web-case-study-mobile-ratio);
  border: 7px solid #10141d;
  border-radius: 22px;
  box-shadow: 0 12px 26px rgba(16, 24, 40, 0.16);
}
.web-case-study-screenshot-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--web-case-study-image-position, top center);
}
.web-case-study-screenshot-body {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  min-height: 54px;
  padding: 12px 16px;
}
.web-case-study-screenshot-icon {
  color: var(--web-case-study-text);
}
.web-case-study-screenshot-title {
  margin: 0;
  color: var(--web-case-study-text);
  font-size: 15px;
  line-height: 1.25;
  font-weight: 750;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-project-context {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(240px, 1fr);
  gap: clamp(16px, 2vw, 28px);
  align-items: stretch;
  margin-bottom: clamp(34px, 5vw, 66px);
}
.web-case-study-project-context--single {
  grid-template-columns: minmax(0, 1fr);
}
.web-case-study-project-tile {
  min-width: 0;
  padding: clamp(22px, 3vw, 34px);
  border: 1px solid var(--web-case-study-border);
  border-radius: 8px;
  background: var(--web-case-study-surface);
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.05);
}
.web-case-study-project-tags {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 10px;
}
.web-case-study-project-tag {
  display: inline-flex;
  min-height: 34px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--web-case-study-project-tag-bg, var(--web-case-study-accent-soft));
  color: var(--web-case-study-project-tag-text, var(--web-case-study-accent));
  font-size: 13px;
  line-height: 1.2;
  font-weight: 850;
  letter-spacing: 0;
  padding: 7px 14px;
  text-align: left;
  overflow-wrap: anywhere;
}
.web-case-study-results {
  margin-bottom: clamp(34px, 5vw, 66px);
}
.web-case-study-results-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(16px, 2vw, 28px);
}
.web-case-study-result-card {
  --web-case-study-result-accent: var(--web-case-study-accent);
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  min-width: 0;
  min-height: 128px;
  padding: 22px;
  border: 1px solid var(--web-case-study-border);
  border-radius: 8px;
  background: var(--web-case-study-surface);
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.05);
}
.web-case-study-result-icon {
  display: inline-flex;
  width: 70px;
  height: 70px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: color-mix(in srgb, var(--web-case-study-result-accent) 10%, #ffffff);
  color: var(--web-case-study-result-accent);
}
.web-case-study-result-value {
  margin: 0;
  color: var(--web-case-study-result-accent);
  font-size: clamp(23px, 2.1vw, 32px);
  line-height: 1.05;
  font-weight: 950;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-result-label {
  margin: 7px 0 0;
  color: var(--web-case-study-text);
  font-size: 13px;
  line-height: 1.3;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-result-description {
  margin: 4px 0 0;
  color: var(--web-case-study-text-soft);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-services {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: clamp(34px, 5vw, 66px);
}
.web-case-study-services-title {
  margin: 0 12px 0 0;
  color: var(--web-case-study-text);
  font-size: clamp(22px, 2vw, 28px);
  line-height: 1.15;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-service-chip {
  display: inline-flex;
  min-height: 32px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--web-case-study-service-bg, var(--web-case-study-accent-soft));
  color: var(--web-case-study-service-text, var(--web-case-study-accent));
  font-size: 13px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  padding: 0 22px;
  overflow-wrap: anywhere;
}
.web-case-study-cta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  padding: clamp(26px, 3vw, 38px) clamp(24px, 4vw, 64px);
  border: 1px solid color-mix(in srgb, var(--web-case-study-accent) 12%, var(--web-case-study-border));
  border-radius: 8px;
  background:
    linear-gradient(90deg, color-mix(in srgb, var(--web-case-study-accent) 8%, #ffffff), #ffffff),
    var(--web-case-study-surface);
}
.web-case-study-cta-content {
  min-width: 0;
}
.web-case-study-cta-title {
  margin: 0;
  color: var(--web-case-study-text);
  font-size: clamp(21px, 2vw, 27px);
  line-height: 1.18;
  font-weight: 900;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-cta-description {
  margin: 9px 0 0;
  color: var(--web-case-study-text-soft);
  font-size: 15px;
  line-height: 1.4;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.web-case-study-cta-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 16px;
}
@supports not (background: color-mix(in srgb, red 10%, white)) {
  .web-case-study-action {
    --web-case-study-action-border: var(--web-case-study-border);
  }
  .web-case-study-stat-icon,
  .web-case-study-result-icon {
    background: var(--web-case-study-accent-soft);
  }
  .web-case-study-cta {
    border-color: var(--web-case-study-border);
    background: var(--web-case-study-surface-soft);
  }
}
@container (max-width: 1120px) {
  .web-case-study-hero {
    grid-template-columns: minmax(0, 1fr);
  }
  .web-case-study-hero-content {
    max-width: 820px;
  }
  .web-case-study-hero-media {
    min-height: clamp(330px, 58vw, 540px);
    grid-template-columns: minmax(0, 1fr) clamp(122px, 16vw, 184px);
  }
  .web-case-study-stats,
  .web-case-study-results-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .web-case-study-stat:nth-child(2n) {
    border-right: 0;
  }
  .web-case-study-stat:nth-child(n + 3) {
    border-top: 1px solid var(--web-case-study-border);
  }
  .web-case-study-screenshot-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@container (max-width: 840px) {
  .web-case-study-main-grid {
    grid-template-columns: 1fr;
  }
  .web-case-study-project-context {
    grid-template-columns: 1fr;
  }
  .web-case-study-delivered {
    padding-left: 0;
    border-left: 0;
  }
  .web-case-study-cta {
    grid-template-columns: 1fr;
  }
  .web-case-study-cta-actions {
    justify-content: flex-start;
  }
}
@container (max-width: 640px) {
  .web-case-study-shell {
    padding: 22px 16px 34px;
  }
  .web-case-study-hero {
    padding-top: 10px;
  }
  .web-case-study-breadcrumb {
    margin-bottom: 22px;
    font-size: 12px;
  }
  .web-case-study-title {
    font-size: 38px;
  }
  .web-case-study-description {
    margin-top: 18px;
    font-size: 16px;
  }
  .web-case-study-action {
    width: 100%;
    min-height: 54px;
    padding: 0 18px;
  }
  .web-case-study-hero-media {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    min-height: 0;
    padding: 10px 0 0;
  }
  .web-case-study-hero-media::before {
    top: 34%;
    right: auto;
    left: 50%;
    width: min(86%, 380px);
    transform: translate(-50%, -50%);
  }
  .web-case-study-laptop {
    width: 100%;
    max-width: 520px;
    justify-self: center;
  }
  .web-case-study-phone {
    width: clamp(112px, 36vw, 152px);
    max-width: none;
    justify-self: end;
    margin-right: clamp(12px, 7vw, 42px);
    transform: none;
  }
  .web-case-study-stats,
  .web-case-study-results-grid,
  .web-case-study-screenshot-grid {
    grid-template-columns: 1fr;
  }
  .web-case-study-stat {
    border-right: 0;
    border-top: 1px solid var(--web-case-study-border);
  }
  .web-case-study-stat:first-child {
    border-top: 0;
  }
  .web-case-study-detail-panel {
    grid-template-columns: 1fr;
  }
  .web-case-study-section-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .web-case-study-screenshot-card--mobile .web-case-study-screenshot-media {
    width: min(48%, 140px);
  }
  .web-case-study-result-card {
    grid-template-columns: 62px minmax(0, 1fr);
    min-height: 112px;
    padding: 20px;
  }
  .web-case-study-result-icon {
    width: 62px;
    height: 62px;
  }
  .web-case-study-services {
    align-items: flex-start;
    flex-direction: column;
  }
  .web-case-study-services-title {
    margin-right: 0;
  }
}
`;

function joinClassNames(...classNames: Array<string | false | null | undefined>) {
    return classNames.filter(Boolean).join(' ');
}

function hasContent(value: React.ReactNode) {
    return value !== undefined && value !== null && value !== false && value !== '';
}

function resolveBreadcrumbCrumbs(
    breadcrumbItems: WebCaseStudyBreadcrumbItem[] | undefined,
    breadcrumbLabel: React.ReactNode,
    breadcrumbHref: string | undefined,
    projectName: React.ReactNode,
): Crumb[] {
    if (Array.isArray(breadcrumbItems)) {
        return breadcrumbItems
            .filter((item) => hasContent(item.label))
            .map((item) => ({
                title: item.label,
                href: item.href,
            }));
    }

    const fallbackCrumbs: Crumb[] = [];

    if (hasContent(breadcrumbLabel)) {
        fallbackCrumbs.push({
            title: breadcrumbLabel,
            href: breadcrumbHref,
        });
    }

    if (hasContent(projectName)) {
        fallbackCrumbs.push({
            title: projectName,
        });
    }

    return fallbackCrumbs;
}

function toCssSize(value?: number | string) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !React.isValidElement(value);
}

function normalizeImage(
    image: WebCaseStudyImageSource | undefined,
    fallbackAlt: string,
    fallbackPosition?: string,
    loading: 'eager' | 'lazy' = 'lazy',
): NormalizedImage {
    if (typeof image === 'string') {
        return {
            src: image,
            alt: fallbackAlt,
            loading,
            decorative: false,
            position: fallbackPosition,
        };
    }

    return {
        src: image?.src || image?.url || image?.original || image?.thumbnail,
        alt: image?.decorative ? '' : image?.alt || image?.title || fallbackAlt,
        title: image?.title,
        width: image?.width,
        height: image?.height,
        loading: image?.loading || loading,
        decorative: Boolean(image?.decorative),
        position: image?.objectPosition || image?.position || fallbackPosition,
    };
}

function renderIcon(
    icon?: React.ReactNode,
    iconToken?: WebCaseStudyIconToken,
    size = 18,
    className = 'web-case-study-icon',
    iconComponent?: WebCaseStudyIconRenderer,
) {
    const iconStyle = { '--web-case-study-icon-size': `${size}px` } as CSSProperties;

    if (icon) {
        return (
            <span className={className} style={iconStyle} aria-hidden="true">
                {icon}
            </span>
        );
    }

    if (iconComponent) {
        const IconComponent = iconComponent;
        return (
            <span className={className} style={iconStyle} aria-hidden="true">
                <IconComponent aria-hidden={true} focusable={false} />
            </span>
        );
    }

    if (!iconToken) return null;

    const Icon = iconRegistry[iconToken];

    return (
        <span className={className} style={iconStyle} aria-hidden="true">
            <Icon size={size} strokeWidth={2.35} aria-hidden="true" />
        </span>
    );
}

function renderAction(
    action: WebCaseStudyAction | false | undefined,
    Link: WebCaseStudyLinkComponent,
    fallbackVariant: WebCaseStudyActionVariant,
    className?: string,
) {
    if (!action) return null;

    const variant = action.variant || fallbackVariant;

    return (
        <Link
            href={action.href}
            className={joinClassNames(
                'web-case-study-action',
                `web-case-study-action--${variant}`,
                className,
            )}
            aria-label={action.ariaLabel}
            title={action.title}
            target={action.target}
            rel={action.rel}
        >
            {renderIcon(
                action.icon,
                action.iconToken,
                18,
                'web-case-study-icon',
                action.iconComponent,
            )}
            <span className="web-case-study-action-text">{action.label}</span>
            {renderIcon(undefined, 'arrowRight', 17)}
        </Link>
    );
}

function DeviceImage({
    image,
    placeholderLabel,
}: {
    image: NormalizedImage;
    placeholderLabel: string;
}) {
    if (!image.src) {
        return (
            <span
                className="web-case-study-device-placeholder"
                role="img"
                aria-label={placeholderLabel}
            />
        );
    }

    return (
        <img
            className="web-case-study-device-image"
            src={image.src}
            alt={image.alt}
            title={image.title}
            width={image.width}
            height={image.height}
            loading={image.loading}
            style={
                {
                    '--web-case-study-image-position': image.position,
                } as WebCaseStudyStyle
            }
        />
    );
}

function WebCaseStudyHero({
    Link,
    breadcrumbItems,
    breadcrumbLabel,
    breadcrumbHref,
    projectName,
    title,
    titleHighlight,
    TitleTag,
    description,
    heroDesktopImage,
    heroMobileImage,
    heroDesktopAlt,
    heroMobileAlt,
    heroDesktopImagePosition,
    heroMobileImagePosition,
    primaryAction,
    secondaryAction,
}: {
    Link: WebCaseStudyLinkComponent;
    breadcrumbItems?: WebCaseStudyBreadcrumbItem[];
    breadcrumbLabel?: React.ReactNode;
    breadcrumbHref?: string;
    projectName?: React.ReactNode;
    title?: React.ReactNode;
    titleHighlight?: React.ReactNode;
    TitleTag: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    heroDesktopImage?: WebCaseStudyImageSource;
    heroMobileImage?: WebCaseStudyImageSource;
    heroDesktopAlt?: string;
    heroMobileAlt?: string;
    heroDesktopImagePosition?: string;
    heroMobileImagePosition?: string;
    primaryAction?: WebCaseStudyAction | false;
    secondaryAction?: WebCaseStudyAction | false;
}) {
    const desktopImage = normalizeImage(
        heroDesktopImage,
        heroDesktopAlt || 'Desktop website preview',
        heroDesktopImagePosition || 'top center',
        'eager',
    );
    const mobileImage = normalizeImage(
        heroMobileImage,
        heroMobileAlt || 'Mobile website preview',
        heroMobileImagePosition || 'top center',
        'eager',
    );
    const breadcrumbCrumbs = resolveBreadcrumbCrumbs(
        breadcrumbItems,
        breadcrumbLabel,
        breadcrumbHref,
        projectName,
    );

    return (
        <div className="web-case-study-hero">
            <div className="web-case-study-hero-content">
                {breadcrumbCrumbs.length > 0 ? (
                    <div className="web-case-study-breadcrumb">
                        <Breadcrumbs
                            crumbs={breadcrumbCrumbs}
                            LinkComponent={Link}
                            className="web-case-study-breadcrumb-nav"
                            ariaLabel="Project breadcrumb"
                            showHomeIcon={false}
                            homeAccent="var(--web-case-study-accent)"
                            currentPageAccent="var(--web-case-study-text)"
                        />
                    </div>
                ) : null}

                {hasContent(title) || hasContent(titleHighlight) ? (
                    <TitleTag className="web-case-study-title">
                        {title}
                        {hasContent(titleHighlight) ? (
                            <>
                                {' '}
                                <span className="web-case-study-title-highlight">
                                    {titleHighlight}
                                </span>
                            </>
                        ) : null}
                    </TitleTag>
                ) : null}

                {hasContent(description) ? (
                    <p className="web-case-study-description">{description}</p>
                ) : null}

                {primaryAction || secondaryAction ? (
                    <div className="web-case-study-actions">
                        {renderAction(primaryAction, Link, 'solid')}
                        {renderAction(secondaryAction, Link, 'outline')}
                    </div>
                ) : null}
            </div>

            <div className="web-case-study-hero-media" aria-hidden="true">
                <div className="web-case-study-laptop">
                    <div className="web-case-study-laptop-frame">
                        <span className="web-case-study-laptop-camera" />
                        <div className="web-case-study-laptop-screen">
                            <DeviceImage
                                image={desktopImage}
                                placeholderLabel="Desktop website preview placeholder"
                            />
                        </div>
                    </div>
                    <div className="web-case-study-laptop-base" />
                </div>

                <div className="web-case-study-phone">
                    <div className="web-case-study-phone-screen">
                        <DeviceImage
                            image={mobileImage}
                            placeholderLabel="Mobile website preview placeholder"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function WebCaseStudyStats({ stats }: { stats: WebCaseStudyStat[] }) {
    if (!stats.length) return null;

    return (
        <div className="web-case-study-stats">
            {stats.map((stat, index) => {
                const accent = stat.accentColor || stat.accentColour;

                return (
                    <article
                        className="web-case-study-stat"
                        style={
                            {
                                '--web-case-study-stat-accent': accent,
                            } as WebCaseStudyStyle
                        }
                        aria-label={stat.ariaLabel}
                        key={stat.id || index}
                    >
                        <span className="web-case-study-stat-icon">
                            {renderIcon(
                                stat.icon,
                                stat.iconToken,
                                34,
                                'web-case-study-icon',
                                stat.iconComponent,
                            )}
                        </span>
                        <div>
                            <p className="web-case-study-stat-value">{stat.value}</p>
                            {hasContent(stat.label) ? (
                                <p className="web-case-study-stat-label">{stat.label}</p>
                            ) : null}
                            {hasContent(stat.description) ? (
                                <p className="web-case-study-stat-label">{stat.description}</p>
                            ) : null}
                        </div>
                    </article>
                );
            })}
        </div>
    );
}

function normalizeChecklistItem(item: WebCaseStudyChecklistItem) {
    if (isObjectRecord(item) && 'label' in item) {
        return item as Exclude<WebCaseStudyChecklistItem, React.ReactNode>;
    }

    return { label: item };
}

function WebCaseStudyOverview({
    overviewTitle,
    overview,
    overviewParagraphs,
    details,
    detailsIcon,
    detailsIconComponent,
    detailsIconToken,
    deliveredTitle,
    deliveredItems,
}: {
    overviewTitle?: React.ReactNode;
    overview?: React.ReactNode;
    overviewParagraphs: React.ReactNode[];
    details: WebCaseStudyDetail[];
    detailsIcon?: React.ReactNode;
    detailsIconComponent?: WebCaseStudyIconRenderer;
    detailsIconToken?: WebCaseStudyIconToken;
    deliveredTitle?: React.ReactNode;
    deliveredItems: WebCaseStudyChecklistItem[];
}) {
    const hasOverviewContent = hasContent(overview) || overviewParagraphs.length > 0;

    if (!hasOverviewContent && !details.length && !deliveredItems.length) return null;

    return (
        <div className="web-case-study-main-grid">
            <section>
                {hasContent(overviewTitle) ? (
                    <h2 className="web-case-study-section-title">{overviewTitle}</h2>
                ) : null}

                {hasOverviewContent ? (
                    <div className="web-case-study-rich-text">
                        {hasContent(overview) ? (
                            React.isValidElement(overview) ? (
                                overview
                            ) : (
                                <p>{overview}</p>
                            )
                        ) : null}

                        {overviewParagraphs.map((paragraph, index) =>
                            React.isValidElement(paragraph) ? (
                                <React.Fragment key={index}>{paragraph}</React.Fragment>
                            ) : (
                                <p key={index}>{paragraph}</p>
                            ),
                        )}
                    </div>
                ) : null}

                {details.length ? (
                    <div className="web-case-study-detail-panel">
                        <span className="web-case-study-detail-icon">
                            {renderIcon(
                                detailsIcon,
                                detailsIconToken || 'store',
                                32,
                                'web-case-study-icon',
                                detailsIconComponent,
                            )}
                        </span>
                        <dl className="web-case-study-detail-list">
                            {details.map((detail, index) => (
                                <div className="web-case-study-detail-row" key={detail.id || index}>
                                    <dt className="web-case-study-detail-label">{detail.label}</dt>
                                    <dd>{detail.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ) : null}
            </section>

            {deliveredItems.length ? (
                <section className="web-case-study-delivered">
                    {hasContent(deliveredTitle) ? (
                        <h2 className="web-case-study-section-title">{deliveredTitle}</h2>
                    ) : null}
                    <ul className="web-case-study-check-list">
                        {deliveredItems.map((item, index) => {
                            const checklistItem = normalizeChecklistItem(item);

                            return (
                                <li
                                    className="web-case-study-check-item"
                                    aria-label={checklistItem.ariaLabel}
                                    key={checklistItem.id || index}
                                >
                                    <span className="web-case-study-check-icon">
                                        {renderIcon(
                                            checklistItem.icon,
                                            checklistItem.iconToken || 'check',
                                            18,
                                            'web-case-study-icon',
                                            checklistItem.iconComponent,
                                        )}
                                    </span>
                                    <span>{checklistItem.label}</span>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}
        </div>
    );
}

function WebCaseStudyScreenshots({
    Link,
    screenshotsTitle,
    screenshotsAction,
    screenshots,
}: {
    Link: WebCaseStudyLinkComponent;
    screenshotsTitle?: React.ReactNode;
    screenshotsAction?: WebCaseStudyAction | false;
    screenshots: WebCaseStudyScreenshot[];
}) {
    if (!screenshots.length) return null;

    return (
        <section className="web-case-study-screenshots">
            <div className="web-case-study-section-header">
                {hasContent(screenshotsTitle) ? (
                    <h2 className="web-case-study-section-title">{screenshotsTitle}</h2>
                ) : null}
                {renderAction(screenshotsAction, Link, 'ghost')}
            </div>

            <div className="web-case-study-screenshot-grid">
                {screenshots.map((screenshot, index) => {
                    const viewport = screenshot.viewport || 'desktop';
                    const image = normalizeImage(
                        screenshot.image,
                        screenshot.alt ||
                            (typeof screenshot.title === 'string'
                                ? screenshot.title
                                : 'Website screenshot'),
                        screenshot.imagePosition || 'top center',
                    );

                    return (
                        <article
                            className={joinClassNames(
                                'web-case-study-screenshot-card',
                                viewport === 'mobile' && 'web-case-study-screenshot-card--mobile',
                            )}
                            key={screenshot.id || index}
                        >
                            <div className="web-case-study-screenshot-media-wrap">
                                <div className="web-case-study-screenshot-media">
                                    {image.src ? (
                                        <img
                                            className="web-case-study-screenshot-image"
                                            src={image.src}
                                            alt={image.alt}
                                            title={image.title}
                                            width={image.width}
                                            height={image.height}
                                            loading={image.loading}
                                            style={
                                                {
                                                    '--web-case-study-image-position':
                                                        image.position,
                                                } as WebCaseStudyStyle
                                            }
                                        />
                                    ) : (
                                        <span
                                            className="web-case-study-device-placeholder"
                                            role="img"
                                            aria-label={`${viewport} screenshot placeholder`}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="web-case-study-screenshot-body">
                                <span className="web-case-study-screenshot-icon">
                                    {renderIcon(
                                        screenshot.icon,
                                        screenshot.iconToken ||
                                            (viewport === 'mobile' ? 'smartphone' : 'monitor'),
                                        21,
                                        'web-case-study-icon',
                                        screenshot.iconComponent,
                                    )}
                                </span>
                                <p className="web-case-study-screenshot-title">
                                    {screenshot.title}
                                </p>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

function normalizeProjectTag(tag: WebCaseStudyProjectTag) {
    if (isObjectRecord(tag) && 'label' in tag) {
        return tag as Exclude<WebCaseStudyProjectTag, React.ReactNode>;
    }

    return { label: tag };
}

function WebCaseStudyProjectContext({
    projectInsightsTitle,
    projectInsights,
    projectInsightParagraphs,
    projectTagsTitle,
    projectTags,
}: {
    projectInsightsTitle?: React.ReactNode;
    projectInsights?: React.ReactNode;
    projectInsightParagraphs: React.ReactNode[];
    projectTagsTitle?: React.ReactNode;
    projectTags: WebCaseStudyProjectTag[];
}) {
    const hasInsightsContent = hasContent(projectInsights) || projectInsightParagraphs.length > 0;
    const hasTagsContent = projectTags.length > 0;

    if (!hasInsightsContent && !hasTagsContent) return null;

    return (
        <section
            className={joinClassNames(
                'web-case-study-project-context',
                (!hasInsightsContent || !hasTagsContent) &&
                    'web-case-study-project-context--single',
            )}
        >
            {hasInsightsContent ? (
                <article className="web-case-study-project-tile">
                    {hasContent(projectInsightsTitle) ? (
                        <h2 className="web-case-study-section-title">{projectInsightsTitle}</h2>
                    ) : null}

                    {hasContent(projectInsights) || projectInsightParagraphs.length ? (
                        <div className="web-case-study-rich-text">
                            {hasContent(projectInsights) ? (
                                React.isValidElement(projectInsights) ? (
                                    projectInsights
                                ) : (
                                    <p>{projectInsights}</p>
                                )
                            ) : null}

                            {projectInsightParagraphs.map((paragraph, index) =>
                                React.isValidElement(paragraph) ? (
                                    <React.Fragment key={index}>{paragraph}</React.Fragment>
                                ) : (
                                    <p key={index}>{paragraph}</p>
                                ),
                            )}
                        </div>
                    ) : null}
                </article>
            ) : null}

            {hasTagsContent ? (
                <aside className="web-case-study-project-tile" aria-label="Project tags">
                    {hasContent(projectTagsTitle) ? (
                        <h2 className="web-case-study-section-title">{projectTagsTitle}</h2>
                    ) : null}

                    {projectTags.length ? (
                        <div className="web-case-study-project-tags">
                            {projectTags.map((tag, index) => {
                                const projectTag = normalizeProjectTag(tag);

                                return (
                                    <span
                                        className="web-case-study-project-tag"
                                        style={
                                            {
                                                '--web-case-study-project-tag-bg':
                                                    projectTag.backgroundColor ||
                                                    projectTag.backgroundColour,
                                                '--web-case-study-project-tag-text':
                                                    projectTag.textColor || projectTag.textColour,
                                            } as WebCaseStudyStyle
                                        }
                                        aria-label={projectTag.ariaLabel}
                                        key={projectTag.id || index}
                                    >
                                        {projectTag.label}
                                    </span>
                                );
                            })}
                        </div>
                    ) : null}
                </aside>
            ) : null}
        </section>
    );
}

function WebCaseStudyResults({
    resultsTitle,
    results,
}: {
    resultsTitle?: React.ReactNode;
    results: WebCaseStudyResult[];
}) {
    if (!results.length) return null;

    return (
        <section className="web-case-study-results">
            {hasContent(resultsTitle) ? (
                <h2 className="web-case-study-section-title">{resultsTitle}</h2>
            ) : null}
            <div className="web-case-study-results-grid">
                {results.map((result, index) => {
                    const accent = result.accentColor || result.accentColour;

                    return (
                        <article
                            className="web-case-study-result-card"
                            style={
                                {
                                    '--web-case-study-result-accent': accent,
                                } as WebCaseStudyStyle
                            }
                            aria-label={result.ariaLabel}
                            key={result.id || index}
                        >
                            <span className="web-case-study-result-icon">
                                {renderIcon(
                                    result.icon,
                                    result.iconToken,
                                    34,
                                    'web-case-study-icon',
                                    result.iconComponent,
                                )}
                            </span>
                            <div>
                                <p className="web-case-study-result-value">{result.value}</p>
                                {hasContent(result.label) ? (
                                    <p className="web-case-study-result-label">{result.label}</p>
                                ) : null}
                                {hasContent(result.description) ? (
                                    <p className="web-case-study-result-description">
                                        {result.description}
                                    </p>
                                ) : null}
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

function normalizeService(service: WebCaseStudyService) {
    if (isObjectRecord(service) && 'label' in service) {
        return service as Exclude<WebCaseStudyService, React.ReactNode>;
    }

    return { label: service };
}

function WebCaseStudyServices({
    servicesTitle,
    services,
}: {
    servicesTitle?: React.ReactNode;
    services: WebCaseStudyService[];
}) {
    if (!services.length && !hasContent(servicesTitle)) return null;

    return (
        <section className="web-case-study-services" aria-label="Services included">
            {hasContent(servicesTitle) ? (
                <h2 className="web-case-study-services-title">{servicesTitle}</h2>
            ) : null}

            {services.map((service, index) => {
                const chip = normalizeService(service);

                return (
                    <span
                        className="web-case-study-service-chip"
                        style={
                            {
                                '--web-case-study-service-bg':
                                    chip.backgroundColor || chip.backgroundColour,
                                '--web-case-study-service-text': chip.textColor || chip.textColour,
                            } as WebCaseStudyStyle
                        }
                        aria-label={chip.ariaLabel}
                        key={chip.id || index}
                    >
                        {chip.label}
                    </span>
                );
            })}
        </section>
    );
}

function WebCaseStudyCtaSection({
    Link,
    cta,
}: {
    Link: WebCaseStudyLinkComponent;
    cta: WebCaseStudyCta | false | undefined;
}) {
    if (!cta) return null;

    const hasActions = cta.primaryAction || cta.secondaryAction;

    return (
        <section className="web-case-study-cta">
            <div className="web-case-study-cta-content">
                {hasContent(cta.title) ? (
                    <h2 className="web-case-study-cta-title">{cta.title}</h2>
                ) : null}
                {hasContent(cta.description) ? (
                    <p className="web-case-study-cta-description">{cta.description}</p>
                ) : null}
            </div>

            {hasActions ? (
                <div className="web-case-study-cta-actions">
                    {renderAction(cta.primaryAction, Link, 'solid')}
                    {renderAction(cta.secondaryAction, Link, 'outline')}
                </div>
            ) : null}
        </section>
    );
}

function WebCaseStudy({
    Link = DefaultLink,
    breadcrumbItems,
    breadcrumbLabel = 'Projects',
    breadcrumbHref,
    projectName,
    title = 'Website Built for',
    titleHighlight = 'Growth.',
    titleComponent = 'h1',
    description,
    heroDesktopImage,
    heroMobileImage,
    heroDesktopAlt,
    heroMobileAlt,
    heroDesktopImagePosition,
    heroMobileImagePosition,
    primaryAction,
    secondaryAction,
    stats = defaultStats,
    overviewTitle = 'Project Overview',
    overview,
    overviewParagraphs = [],
    details = [],
    detailsIcon,
    detailsIconComponent,
    detailsIconToken = 'store',
    deliveredTitle = 'What Was Delivered',
    deliveredItems = defaultDeliveredItems,
    screenshotsTitle = 'Website Screenshots',
    screenshotsAction,
    screenshots = [],
    projectInsightsTitle = 'Project Insights',
    projectInsights,
    projectInsightParagraphs = [],
    projectTagsTitle = 'Project Tags',
    projectTags = [],
    resultsTitle = 'Results & Impact',
    results = defaultResults,
    servicesTitle = 'Services Included',
    services = [],
    cta,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    className,
    style,
    id,
    ariaLabel,
}: WebCaseStudyProps) {
    const resolvedColors = { ...colours, ...colors };
    const resolvedAccent = accentColor || accentColour || resolvedColors.accent;
    const TitleTag = titleComponent;
    const sectionStyle: WebCaseStudyStyle = {
        '--web-case-study-bg':
            backgroundColor || backgroundColour || resolvedColors.background || undefined,
        '--web-case-study-surface':
            surfaceColor || surfaceColour || resolvedColors.surface || undefined,
        '--web-case-study-surface-soft': resolvedColors.surfaceSoft,
        '--web-case-study-text': textColor || textColour || resolvedColors.text || undefined,
        '--web-case-study-text-soft': resolvedColors.textSoft,
        '--web-case-study-text-muted': resolvedColors.textMuted,
        '--web-case-study-accent': resolvedAccent,
        '--web-case-study-accent-strong': resolvedColors.accentStrong,
        '--web-case-study-accent-soft': resolvedColors.accentSoft,
        '--web-case-study-border': resolvedColors.border,
        '--web-case-study-shadow': resolvedColors.shadow,
        '--web-case-study-max-width': toCssSize(maxWidth),
        ...style,
    };

    return (
        <section
            className={joinClassNames('web-case-study', className)}
            style={sectionStyle}
            id={id}
            aria-label={ariaLabel}
        >
            <style>{webCaseStudyCss}</style>

            <div className="web-case-study-shell">
                <WebCaseStudyHero
                    Link={Link}
                    breadcrumbItems={breadcrumbItems}
                    breadcrumbLabel={breadcrumbLabel}
                    breadcrumbHref={breadcrumbHref}
                    projectName={projectName}
                    title={title}
                    titleHighlight={titleHighlight}
                    TitleTag={TitleTag}
                    description={description}
                    heroDesktopImage={heroDesktopImage}
                    heroMobileImage={heroMobileImage}
                    heroDesktopAlt={heroDesktopAlt}
                    heroMobileAlt={heroMobileAlt}
                    heroDesktopImagePosition={heroDesktopImagePosition}
                    heroMobileImagePosition={heroMobileImagePosition}
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                />

                <WebCaseStudyStats stats={stats} />

                <WebCaseStudyOverview
                    overviewTitle={overviewTitle}
                    overview={overview}
                    overviewParagraphs={overviewParagraphs}
                    details={details}
                    detailsIcon={detailsIcon}
                    detailsIconComponent={detailsIconComponent}
                    detailsIconToken={detailsIconToken}
                    deliveredTitle={deliveredTitle}
                    deliveredItems={deliveredItems}
                />

                <WebCaseStudyScreenshots
                    Link={Link}
                    screenshotsTitle={screenshotsTitle}
                    screenshotsAction={screenshotsAction}
                    screenshots={screenshots}
                />

                <WebCaseStudyProjectContext
                    projectInsightsTitle={projectInsightsTitle}
                    projectInsights={projectInsights}
                    projectInsightParagraphs={projectInsightParagraphs}
                    projectTagsTitle={projectTagsTitle}
                    projectTags={projectTags}
                />

                <WebCaseStudyResults resultsTitle={resultsTitle} results={results} />

                <WebCaseStudyServices servicesTitle={servicesTitle} services={services} />

                <WebCaseStudyCtaSection Link={Link} cta={cta} />
            </div>
        </section>
    );
}

export default WebCaseStudy;
